import React, { useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Card from '@/components/ui/Card';
import { PlayerSeasonStats, PlayerTrendPoint } from '@/types';
import ChartTableToggle from './ChartTableToggle';

export interface PlayerTrendChartProps {
  player: PlayerSeasonStats;
  trend: PlayerTrendPoint[];
  summary: string;
  limit?: number;
  truncated?: boolean;
}

const availability = 'All listed metrics supplied';

const PlayerTrendChart: React.FC<PlayerTrendChartProps> = ({ player, trend, summary, limit, truncated }) => {
  const [mode, setMode] = useState<'chart' | 'table'>('chart');
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const summaryId = `player-trend-summary-${player.playerId}`;
  return <Card>
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-gray-950 dark:text-white">{player.shortName || player.playerName} trend</h2>
        <p id={summaryId} className="mt-1 text-sm text-gray-600 dark:text-gray-300">{summary}</p>
        {truncated && <p role="status" className="mt-1 text-sm font-medium text-amber-700 dark:text-amber-300">Only the latest {limit} trend records are shown.</p>}
      </div>
      <ChartTableToggle value={mode} onChange={setMode} label="Player trend display" />
    </div>
    {trend.length === 0 ? <p className="text-sm text-gray-600 dark:text-gray-300">No match trend is available for this player.</p> : mode === 'chart' ? <>
      <div className="h-80 min-w-0" role="img" aria-describedby={summaryId} aria-label={`Match trend for ${player.playerName}`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={trend} accessibilityLayer>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" interval="preserveStartEnd" />
            <YAxis yAxisId="totals" />
            <YAxis yAxisId="minutes" orientation="right" />
            <Tooltip /><Legend />
            <Bar yAxisId="totals" dataKey="goals" name="Goals" fill="#2563eb" isAnimationActive={!reducedMotion} />
            <Bar yAxisId="totals" dataKey="assists" name="Assists" fill="#f97316" isAnimationActive={!reducedMotion} />
            <Line yAxisId="totals" type="monotone" dataKey="xg" name="xG" stroke="#15803d" strokeWidth={2} isAnimationActive={!reducedMotion} />
            <Line yAxisId="totals" type="monotone" dataKey="xgAssist" name="xG assist" stroke="#a21caf" strokeWidth={2} isAnimationActive={!reducedMotion} />
            <Line yAxisId="totals" type="monotone" dataKey="shots" name="Shots" stroke="#0f766e" isAnimationActive={!reducedMotion} />
            <Line yAxisId="totals" type="monotone" dataKey="keyPasses" name="Key passes" stroke="#b45309" isAnimationActive={!reducedMotion} />
            <Line yAxisId="minutes" type="monotone" dataKey="minutes" name="Minutes" stroke="#475569" strokeDasharray="4 3" isAnimationActive={!reducedMotion} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 rounded-lg bg-gray-50 p-3 dark:bg-gray-800/60">
        <h3 className="text-sm font-semibold text-gray-950 dark:text-white">Chart data</h3>
        <ol className="mt-2 grid gap-2 text-sm text-gray-700 dark:text-gray-200 sm:grid-cols-2">
          {trend.map(point => <li key={point.matchId} className="rounded border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
            <span className="font-semibold">{point.label}</span><br />
            Date: {point.date}. Minutes: {point.minutes}. Goals: {point.goals}. Assists: {point.assists}. xG: {point.xg}. xG assist: {point.xgAssist}. Shots: {point.shots}. Key passes: {point.keyPasses}. Availability: {availability}.
          </li>)}
        </ol>
      </div>
    </> : <div className="max-w-full overflow-x-auto">
      <table className="w-full min-w-[62rem] text-sm" aria-describedby={summaryId}>
        <caption className="sr-only">Player match-by-match trend</caption>
        <thead><tr className="border-b border-gray-200 dark:border-gray-700"><th scope="col" className="p-2 text-left">Match</th><th scope="col" className="p-2">Date</th><th scope="col" className="p-2">Minutes</th><th scope="col" className="p-2">Goals</th><th scope="col" className="p-2">Assists</th><th scope="col" className="p-2">xG</th><th scope="col" className="p-2">xG assist</th><th scope="col" className="p-2">Shots</th><th scope="col" className="p-2">Key passes</th><th scope="col" className="p-2">Availability</th></tr></thead>
        <tbody>{trend.map(point => <tr key={point.matchId} className="border-b border-gray-100 text-center dark:border-gray-800"><th scope="row" className="p-2 text-left font-medium">{point.label}</th><td className="p-2">{point.date}</td><td className="p-2">{point.minutes}</td><td className="p-2">{point.goals}</td><td className="p-2">{point.assists}</td><td className="p-2">{point.xg}</td><td className="p-2">{point.xgAssist}</td><td className="p-2">{point.shots}</td><td className="p-2">{point.keyPasses}</td><td className="p-2">{availability}</td></tr>)}</tbody>
      </table>
    </div>}
  </Card>;
};

export default PlayerTrendChart;
